"use client";

import Image from "next/image";
import { BiPhone } from "react-icons/bi";
import { usePathname } from "next/navigation";
import { googleTagManager } from "@/components/common/utils";

const VisitPageHeader = () => {
  const pathname = usePathname();

  // Existing MBBS pages (show phone CTA)
  const mbbsPaths = ["/mbbs/visit", "/mbbs-nepal/visit", "/mbbs-uae/visit"];
  const isMbbsPath = mbbsPaths.includes(pathname);

  // New: Accounting visit pages (show WhatsApp CTA)
  const accountingVisitPaths = [
    "/best-us-cpa-admission-online-course-fees-in-india",
    "/best-us-cma-coaching-in-india",
    "/miles-education-cpa-fees-miles-education-cma",
    "/miles-us-cpa-miles-us-cma",
    "/accounting/events/webinar",
    "/cpa/visit",
    "/cma/visit",
    "/cpa-cma-course/visit",
    "/miles-us-cpa-miles-us-cma-course",
  ];
  const isAccountingVisit = accountingVisitPaths.includes(pathname);

  // Special page with custom phone number
  const isSpecialCoursePage = pathname === "/miles-us-cpa-miles-us-cma-course";
  const phoneNumber = isSpecialCoursePage ? "9513881313" : "9513881313";
  const formattedPhoneNumber = isSpecialCoursePage
    ? "95138 81313"
    : "95138 81313";

  // GTM event mapping for Talk to Expert button
  const gtmEventMap: Record<string, string> = {
    "/best-us-cpa-admission-online-course-fees-in-india":
      "CPA_LP_H_TalkToAnExpert",
    "/miles-us-cpa-miles-us-cma": "Brand_LP_H_TalkToAnExpert",
    "/miles-education-cpa-fees-miles-education-cma":
      "Webinar_LP_H_TalkToAnExpert",
    "/best-us-cma-coaching-in-india": "CMA_LP_H_TalkToAnExpert",
  };

  const handleTalkToExpertClick = () => {
    const eventName = gtmEventMap[pathname];
    if (eventName) {
      googleTagManager(eventName, {
        button_location: "header",
        phone_number: phoneNumber,
        page_url: typeof window !== "undefined" ? window.location.href : "",
      });
    }
  };

  return (
    <>
      {/* DESKTOP / TABLET */}
      <div className="hidden lg:block">
        <div className="fixed top-0 left-0 right-0 bg-[#191919] z-[999]">
          <div className="main_container mx-auto flex justify-between items-center py-4 px-4">
            <Image
              src="https://ik.imagekit.io/mileseducation/miles_website/home/new_logo.webp?tr=w-140,h-43,q-80"
              alt="Miles logo"
              width={140}
              height={43}
              priority
              sizes="140px"
            />

            <div className="flex items-center gap-3">
              {/* MBBS call button (existing behavior) */}
              {isMbbsPath && (
                <a
                  href="tel:919740516790"
                  className="bg-[#39f] text-white rounded-[16px_16px_16px_0] flex items-center font-bold text-base gap-2 px-4 py-2 hover:bg-[#2d7dd8] transition-colors duration-200"
                >
                  <BiPhone className="w-5 h-5" />
                  9740516790
                </a>
              )}

              {/* NEW: Call button for CPA/CMA visit pages */}
              {isAccountingVisit && (
                <a
                  id="header-call-button-desktop"
                  href={`tel:+91${phoneNumber}`}
                  aria-label="Call us for more details"
                  className="bg-[#3399FF] text-white rounded-[50px] flex items-center justify-center font-bold text-base gap-3 px-6 py-[5px] hover:brightness-95 transition-colors duration-200"
                  onClick={handleTalkToExpertClick}
                >
                  <Image
                    src="https://ik.imagekit.io/mileseducation/miles_website/accounting/miles-education-cpa-fees-miles-education-cma/Call.webp"
                    alt="Call icon"
                    className="w-6 h-6"
                    width={40}
                    height={40}
                    priority
                  />
                  <span className="flex flex-col items-start leading-[1]">
                    <span className="text-xs font-normal">
                      Talk to an Expert
                    </span>
                    <span className="text-[24px] font-bold">
                      {formattedPhoneNumber}
                    </span>
                  </span>
                </a>
              )}
            </div>
          </div>
        </div>
        {/* spacer for fixed header height */}
        <div className="h-[70px]" />
      </div>

      {/* MOBILE */}
      <div className="lg:hidden">
        <div className="w-full flex justify-center h-[70px]">
          <div
            className="mx-auto w-[96%] bg-[rgba(19,19,19,0.6)] rounded-full flex px-4 py-3 items-center justify-between fixed top-4 z-[999]"
            style={{
              boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
              backdropFilter: "blur(25px)",
              WebkitBackdropFilter: "blur(25px)",
            }}
          >
            <Image
              src="https://ik.imagekit.io/mileseducation/miles_website/home/new_logo.webp?tr=w-100,h-30,q-80"
              alt="Miles logo"
              width={80}
              height={25}
              priority
              sizes="91px"
            />

            <div className="flex items-center gap-2">
              {/* MBBS call button (existing behavior) */}
              {isMbbsPath && (
                <a
                  href="tel:919740516790"
                  className="bg-[#39f] text-white rounded-[16px_16px_16px_0] flex items-center font-semibold text-sm gap-2 px-4 py-[6px] hover:bg-[#2d7dd8] transition-colors duration-200"
                >
                  <BiPhone className="w-5 h-5" />
                  Call
                </a>
              )}

              {/* NEW: Call button for CPA/CMA visit pages */}
              {isAccountingVisit && (
                <a
                  id="header-call-button-mobile"
                  href={`tel:${phoneNumber}`}
                  aria-label="Call us for more details"
                  className="bg-[#3399FF] text-white rounded-[50px] flex items-center justify-center font-bold text-base gap-2 px-3 py-[3px] hover:brightness-95 transition-colors duration-200"
                  onClick={handleTalkToExpertClick}
                >
                  <Image
                    src="https://ik.imagekit.io/mileseducation/miles_website/accounting/miles-education-cpa-fees-miles-education-cma/Call.webp"
                    alt="Call icon"
                    className="w-4 h-4"
                    width={40}
                    height={40}
                    priority
                  />
                  <span className="flex flex-col items-start leading-[1]">
                    <span className="text-[10px] font-normal">
                      Talk to an Expert
                    </span>
                    <span className="text-[18px] font-bold">
                      {formattedPhoneNumber}
                    </span>
                  </span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VisitPageHeader;
